// /* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from 'components/base/IndeterminateCheckbox';
import { PropsWithChildren } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  InitialTableState,
  Row,
} from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';

interface UseAdvanceTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  selection?: boolean;
  sortable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  selectionColumnWidth?: number | string;
  initialState?: InitialTableState;
  enableRowSelection?: boolean | ((row: any) => boolean);
  /**
   * Mirrors TanStack's own signature, `parent` included. A table with sub-rows
   * MUST use it: the same record can legitimately occupy two rows (its own and a
   * sub-row of an expanded parent), and an id built from the record alone gives
   * both the same React key. See `nodeInstanceRowId`.
   *
   * `NoInfer`: a row-id function consumes the row type, it does not define it.
   * Let `data` drive the inference of `T`; without it a *named* row-id function
   * (rather than an inlined lambda) silently narrows `T` for the whole table,
   * and `row.original` with it.
   */
  getRowId?: (originalRow: NoInfer<T>, index: number, parent?: Row<NoInfer<T>>) => string;
}

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler(),
      }}
    />
  ),
  cell: ({ row }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler(),
      }}
    />
  ),
  meta: {
    headerProps: { style: { width: '30px' } },
  },
};

const useAdvanceTable = <T,>({
  columns,
  data,
  selection,
  sortable,
  pagination,
  pageSize,
  initialState,
  enableRowSelection,
  getRowId,
}: PropsWithChildren<UseAdvanceTableProps<T>>) => {
  const location = useLocation();
  const isNodeInstancesPage = location.pathname === '/automation/node_instances';

  const baseColumns = [...columns];
  const actionsColumns = baseColumns.filter((column: any) => column.id === 'actions');
  const expandColumns = baseColumns.filter((column: any) => column.id === 'expand');
  const dataColumns = baseColumns.filter((column: any) => column.id !== 'actions' && column.id !== 'expand');

  const orderedColumns = isNodeInstancesPage ? [...expandColumns, ...actionsColumns, ...dataColumns] : [...actionsColumns, ...expandColumns, ...dataColumns];

  const state = {
    pagination: { pageSize: pagination ? pageSize : data.length },
    ...initialState,
  };

  const formattedChildNodes = (row: any) => {
    const { child_nodes = [] } = row;
    const newChildNodes = child_nodes.map((node: any) => {
      return {
        ...node,
        node_definition_id: node.definition_id,
      };
    });
    return newChildNodes;
  };

  const table = useReactTable<T>({
    data,
    columns: selection ? [selectionColumn, ...orderedColumns] : orderedColumns,
    enableSorting: sortable,
    enableRowSelection,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(isNodeInstancesPage && {
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: (row) => (row as any)?.original?.has_child_nodes,
      getSubRows: (row) => formattedChildNodes(row),
    }),
    initialState: state,
    enableSortingRemoval: false,
    manualSorting: isNodeInstancesPage ? true : false,
  });

  return table;
};

export default useAdvanceTable;
